/**
 * The registry of queries the client currently has materialised.
 *
 * ┌─ ⛔ WHY A REGISTRY EXISTS AT ALL, AND IT IS NOT CACHING ────────────────────┐
 * │ INBOX item 17 answers "reconnect: replay or refetch" with REFETCH, and the  │
 * │ design decided that, not this session. Three places say so:                 │
 * │                                                                            │
 * │   `worker.ts` on a failed broadcast: "the row is committed but no live      │
 * │   client was told. **A reload will show it; nothing will re-push it.**"     │
 * │                                                                            │
 * │   `worker.ts` again: "there is **no sweeper for a push and there should not │
 * │   be one**, because the inbox reads the database."                          │
 * │                                                                            │
 * │   `broadcast.ts`, on the ids-only payload: "a client that missed a push and │
 * │   reloads gets the identical result **through one code path rather than     │
 * │   two**."                                                                   │
 * │                                                                            │
 * │ A reconnect gap is the same event with a different cause, so answering it a │
 * │ second way would give one failure two recovery models. **Do not             │
 * │ re-litigate this** - see `docs/INBOX-WEBSOCKET-DESIGN.md` question 1.       │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ── ⛔ REFETCH WHAT: EVERYTHING MATERIALISED, AND THE NARROW VERSION IS WRONG ─
 *
 * The tempting narrow refetch - just the open conversation - **is wrong in
 * exactly the case that matters, and it looks correct in every test that has a
 * conversation open.** The event most likely to be missed is a NEW CONVERSATION
 * APPEARING IN A LIST NOBODY IS LOOKING AT. So the unit of refetch is every
 * query currently mounted, and the list is one of them whether or not a thread
 * is open.
 *
 * ── ⛔ THE REGISTRY IS THE FETCH PATH. IT IS NOT A LIST BESIDE ONE. ──────────
 *
 * **You cannot materialise a query without registering it, because registering
 * is how you get the data.** `subscribe()` runs the fetcher, stores the result,
 * and hands it back; there is no other way to obtain it. A hand-maintained
 * enumeration of "queries to refetch" would be the same shape as a mandated
 * query form with nothing enforcing it - correct on the day it is written and
 * silently incomplete afterwards.
 *
 * ⚠️ **WHAT STILL GOES WRONG: A COMPONENT THAT CALLS `fetch` DIRECTLY.** It
 * never enters this store, so it is never refetched, and **that is the
 * missed-event failure arriving through the fix for missed events.** It is
 * silent by construction. A convention will not catch it, so
 * `tests/web-query-registry.test.ts` scans `web/src` for `fetch(` outside this
 * module and fails on anything not exempted with a written reason.
 */

/** The fetcher's result is opaque to the store; only the caller interprets it. */
export type Fetcher<T> = () => Promise<T>

type Entry = {
  fetcher: Fetcher<unknown>
  /** Last successful value, or `undefined` before the first resolve. */
  data: unknown
  /** Notified on every new value. Refcount: an empty set means unmounted. */
  listeners: Set<(data: unknown) => void>
  /**
   * Notified when the fetcher throws.
   *
   * ⛔ A SEPARATE SET, KEYED BY THE DATA LISTENER, so releasing one releases
   * both. A single set of `{onData, onError}` pairs would be tidier and would
   * make the refcount - which decides what a reconnect refetches - depend on a
   * subscriber having supplied both, which is optional.
   */
  errorListeners: Map<(data: unknown) => void, (error: unknown) => void>
  /** The last error, kept so a view can show one without the store deciding. */
  error: unknown
}

const entries = new Map<string, Entry>()

/**
 * How many refetch passes have completed. **Exposed for tests**, and cheap
 * enough to keep: a client that believes it refetched and did not is precisely
 * the failure this whole mechanism exists to prevent, and a counter is the only
 * thing that can tell "refetched and nothing changed" from "never refetched".
 */
let passes = 0
export function refetchPasses(): number {
  return passes
}

/**
 * Subscribe to a query, fetching it if this is the first subscriber.
 *
 * Returns an unsubscribe function. **The caller must call it**: an entry with
 * listeners is "materialised", and one that is never released stays in the
 * refetch set forever, so every reconnect re-runs a query nothing is rendering.
 */
/**
 * ┌─ ⛔ THE ERROR CHANNEL, ADDED 2026-08-08, AND ITS ABSENCE WAS INVISIBLE ─────┐
 * │ `Entry.error` has been stored since this file was written and **handed to  │
 * │ nobody**. `useConversations` declares `error`, `setError(null)` twice and   │
 * │ never anything else; `ConversationList` renders                             │
 * │ `Could not load conversations. {error}` behind `error !== null`.            │
 * │                                                                            │
 * │ ⚠️ **A RENDERED ERROR STATE NO CODE PATH COULD REACH.** The component test  │
 * │ passes it as a prop, so it is green; the empty-state documentation          │
 * │ describes it at length; and in the product a failed fetch showed the        │
 * │ LOADING state forever - which is the exact confusion the three empty states │
 * │ exist to prevent, arriving through the mechanism that documents them.       │
 * │                                                                            │
 * │ ⛔ IT IS THE `captureReturnTo` SHAPE AGAIN: built, documented, unreachable, │
 * │ and findable only by asking what could possibly set the value. Found by the │
 * │ item-6 audit, not by anything failing.                                       │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **A 401 DOES NOT COME THROUGH HERE.** `data/unauthorized.ts` reports it
 * separately, because a 401 is a fact about the SESSION and every other query is
 * about to fail the same way. This channel is for a query that failed on its own
 * - a 500, a parse error, a network drop - which is a fact about that query.
 */
export function subscribe<T>(
  key: string,
  fetcher: Fetcher<T>,
  onData: (data: T) => void,
  onError?: (error: unknown) => void,
): () => void {
  let entry = entries.get(key)
  const first = entry === undefined
  if (!entry) {
    entry = {
      fetcher: fetcher as Fetcher<unknown>,
      data: undefined,
      listeners: new Set(),
      errorListeners: new Map(),
      error: undefined,
    }
    entries.set(key, entry)
  }
  const listener = onData as (data: unknown) => void
  entry.listeners.add(listener)
  if (onError) entry.errorListeners.set(listener, onError)

  // A later subscriber gets what is already there rather than waiting for a
  // round trip, and gets it synchronously so no frame renders empty.
  if (!first && entry.data !== undefined) onData(entry.data as T)
  /**
   * ⚠️ AND A LATER SUBSCRIBER TO A FAILED QUERY LEARNS THAT TOO, synchronously.
   * Without this, mounting a second view of a query that has already failed
   * shows the LOADING state forever - it has no data, and the fetch that would
   * have told it already happened.
   */
  if (!first && entry.error !== undefined) onError?.(entry.error)
  if (first) void run(key)

  let released = false
  return () => {
    // Idempotent: React can call a cleanup twice under StrictMode, and a double
    // release would drop an entry a second component still holds.
    if (released) return
    released = true
    const current = entries.get(key)
    if (!current) return
    current.listeners.delete(listener)
    current.errorListeners.delete(listener)
    /**
     * ┌─ ⛔ RELEASE MEANS RELEASE. IF YOU CAME HERE TO ADD A GRACE PERIOD, READ ─┐
     * │ THIS FIRST. DECIDED 2026-08-07, after a browser session made the         │
     * │ symptom visible.                                                        │
     * │                                                                        │
     * │ ⚠️ THE SYMPTOM: in development, `useConversations` fetches TWICE on      │
     * │ mount. The effect runs, subscribes, and fetches; **React StrictMode      │
     * │ unmounts and immediately remounts**; the entry had no listeners left in  │
     * │ between, so this line deleted it, and the remount finds nothing cached   │
     * │ and fetches again.                                                      │
     * │                                                                        │
     * │ ✅ THE BEHAVIOUR IS CORRECT AND IT DOES NOT SHIP. StrictMode's double    │
     * │ invoke is DEVELOPMENT ONLY - React does not do it in a production build  │
     * │ - so the duplicate request exists only where it is meant to: in the      │
     * │ place that surfaces effects which are not resilient to remounting. Ours  │
     * │ is; it just costs a round trip to prove it.                             │
     * │                                                                        │
     * │ ⛔ WHY EVICTING IMMEDIATELY IS THE DECISION AND NOT THE SHORTCUT. An     │
     * │ entry that outlives its last listener is a LEAK WITH A CONSEQUENCE, not  │
     * │ merely some retained memory: `refetchAll()` re-runs every materialised   │
     * │ query on every reconnect, so a query nothing is rendering would be       │
     * │ refetched forever. **The refetch set has to mean "what is on screen"**,  │
     * │ and the only definition of that which cannot drift is "has a listener".  │
     * │                                                                        │
     * │ ⛔ AND THE USUAL REMEDY IS A TIMER NOBODY CAN CHOOSE. Keeping the entry  │
     * │ for N milliseconds after the last release would collapse the StrictMode  │
     * │ double-fetch, and N is unarguable in either direction: too short and it  │
     * │ does nothing, too long and a closed view keeps costing a request per     │
     * │ reconnect. There is no measurement that produces N, because the thing it │
     * │ trades against - a remount that is not a real unmount - is a DEVELOPMENT │
     * │ ARTEFACT with no production counterpart to measure. This project has     │
     * │ spent real effort removing exactly that kind of constant.                │
     * │                                                                        │
     * │ ⚠️ SO THE COST IS NAMED RATHER THAN HIDDEN: one duplicate request per    │
     * │ mount, in development only, on a query that is idempotent and cheap. If  │
     * │ that ever stops being true - an expensive query, or a real production    │
     * │ remount loop - the answer is to make the CALLER stable, not to make the  │
     * │ store forgetful on a timer.                                             │
     * └──────────────────────────────────────────────────────────────────────────┘
     */
    if (current.listeners.size === 0) entries.delete(key)
  }
}

async function run(key: string): Promise<void> {
  const entry = entries.get(key)
  if (!entry) return
  try {
    const data = await entry.fetcher()
    // ⚠️ RE-READ AFTER THE AWAIT. The entry can have been released while the
    // request was in flight, and writing to a deleted entry would resurrect a
    // query nothing is rendering.
    const still = entries.get(key)
    if (!still) return
    still.data = data
    still.error = undefined
    for (const l of still.listeners) l(data)
  } catch (err) {
    const still = entries.get(key)
    if (!still) return
    still.error = err
    /**
     * ⛔ THE LINE THAT DID NOT EXIST UNTIL 2026-08-08. The error was STORED and
     * never delivered, so `ConversationList`'s error state - written, styled,
     * documented at length and covered by a component test - was unreachable in
     * the product. A failed fetch showed the loading state forever.
     *
     * ⚠️ ONE LISTENER THROWING MUST NOT SKIP THE REST, the same rule
     * `refetchAll` follows. A view that fails while handling a failure would
     * otherwise silence every other view of the same query.
     */
    for (const [dataListener, onError] of still.errorListeners) {
      if (!still.listeners.has(dataListener)) continue
      try {
        onError(err)
      } catch {
        // Deliberately swallowed. See above.
      }
    }
  }
}

/**
 * Re-run every materialised query. **This is what a reconnect calls.**
 *
 * ⚠️ ONE FAILURE MUST NOT STOP THE REST. `run` already swallows per-query
 * errors; this note is here because the obvious `Promise.all` over throwing
 * fetchers would reject on the first and leave the others un-refetched, which
 * is the partial-recovery-that-looks-total shape.
 */
export async function refetchAll(): Promise<number> {
  // Snapshot the keys: a fetcher completing mid-pass can add or remove entries,
  // and iterating the live Map would skip or repeat.
  const keys = [...entries.keys()]
  await Promise.all(keys.map((k) => run(k)))
  passes += 1
  return keys.length
}

/**
 * Re-run ONE materialised query, leaving the rest alone.
 *
 * ⛔ ADDED 2026-08-20 FOR PAGINATION, AND IT IS NOT A NARROW `refetchAll`.
 * A reconnect must refresh everything; **loading another page must refresh
 * nothing else**. Reusing `refetchAll` for a scroll would re-request every open
 * thread and panel on every page turn, which is the opposite of why the list
 * was paginated.
 *
 * ⚠️ IT DOES NOT ADVANCE `passes`. That counter means "how many times has a
 * reconnect swept the store", and `tests/web-query-registry.test.ts` reads it
 * as exactly that. A page turn incrementing it would make the recovery
 * instrument count ordinary scrolling.
 *
 * ⚠️ AN UNKNOWN KEY IS A NO-OP, not an error: the entry can legitimately have
 * been released between a scroll and its handler running.
 */
export async function refetchKey(key: string): Promise<boolean> {
  if (!entries.has(key)) return false
  await run(key)
  return true
}

/** Keys currently materialised. Test seam and the diagnostic for a leak. */
export function materialisedKeys(): string[] {
  return [...entries.keys()].sort()
}

/** Drop everything. Sign-out, and test isolation. */
export function clearQueries(): void {
  entries.clear()
}
